import { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { isAdminSession } from "@/lib/adminAuth";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import {
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  PieChart,
  Pie,
  Legend,
  ComposedChart,
  Line
} from "recharts";
import {
  ArrowLeft,
  Download,
  Share2,
  Printer,
  MapPin,
  Star,
  Globe,
  TrendingUp,
  BarChart3,
  ShoppingCart,
  Cpu,
  Sparkles,
  AlertCircle,
  Loader2,
  Target,
  Shield,
  Users,
  GitCompare,
  ArrowUpRight,
  Lock,
  DollarSign,
  Phone,
  MessageSquare,
  Map,
  Link as LinkIcon
} from "lucide-react";
import Navbar from "@/components/Navbar";
import ScoreCircle from "@/components/ScoreCircle";
import GradeCard from "@/components/GradeCard";
import SectionCard from "@/components/SectionCard";
import BudgetMaker from "@/components/BudgetMaker";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const sectionIcons = {
  listings: MapPin,
  reviews: Star,
  social: Share2,
  website: Globe,
  seo: TrendingUp,
  advertising: BarChart3,
  ecommerce: ShoppingCart,
  tech_stack: Cpu,
  voice: Phone,
  ai_chatbot: MessageSquare,
  mapping: Map,
  linking: LinkIcon
};

const sectionLabels = {
  listings: "Listings",
  reviews: "Reviews",
  social: "Social Media",
  website: "Website",
  seo: "SEO",
  advertising: "Advertising",
  ecommerce: "Ecommerce",
  tech_stack: "Tech Stack",
  voice: "Voice/Phone",
  ai_chatbot: "AI Chatbot",
  mapping: "Mapping",
  linking: "Linking"
};

const gradeColors = {
  A: "#10B981",
  B: "#84CC16",
  C: "#EAB308",
  D: "#F97316",
  F: "#EF4444"
};

const sectionColors = [
  "#6366F1", "#8B5CF6", "#EC4899", "#F43F5E",
  "#F97316", "#EAB308", "#22C55E", "#06B6D4",
  "#14B8A6", "#3B82F6", "#A855F7", "#F472B6"
];

export default function ReportDashboard() {
  const { reportId } = useParams();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState("overview");
  const [gradeView, setGradeView] = useState("operations"); // admin, operations, comparison
  const reportRef = useRef(null);

  useEffect(() => {
    fetchReport();
  }, [reportId]);

  const fetchReport = async () => {
    try {
      const response = await axios.get(`${API}/reports/${reportId}`);
      setReport(response.data);
    } catch (error) {
      console.error("Error fetching report:", error);
      toast.error("Failed to load report");
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => window.print();

  const handleShare = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ title: `Selletrust Report - ${report?.business_name}`, url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success("Report link copied!");
      }
    } catch (err) {
      toast.info(`Share: ${url}`);
    }
  };

  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    document.getElementById(sectionId)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!report) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
          <p className="text-slate-600 mb-4">Report not found</p>
          {isAdminSession() ? (
            <Link to="/reports"><Button variant="outline">View All Reports</Button></Link>
          ) : (
            <Link to="/lookup"><Button variant="outline">Analyze a Business</Button></Link>
          )}
        </div>
      </div>
    );
  }

  // Include new sections if they exist in the report
  const baseSections = ["listings", "reviews", "social", "website", "seo", "advertising", "ecommerce", "tech_stack"];
  const newSections = ["voice", "ai_chatbot", "mapping", "linking"];
  const sections = [...baseSections, ...newSections.filter(s => report[s])];

  // Get grades based on current view
  const getGradeForView = (section) => {
    if (!report[section]) return "N/A";
    if (gradeView === "admin") return report[section].grade;
    if (gradeView === "operations") return report[section].grade_operations || report[section].grade;
    return report[section].grade; // comparison shows current
  };

  const getOverallGrade = () => {
    if (gradeView === "admin") return report.overall_grade;
    if (gradeView === "operations") return report.overall_grade_operations;
    return report.overall_grade;
  };

  // Los reportes de enero no tienen proyeccion; la vista Grade C mostraba "% (+-29)"
  // y "to null". Sin proyeccion, esa vista no se ofrece.
  const tieneProyeccion = report.projected_overall_score != null;

  // Chart data
  const radarData = sections.filter(s => report[s]).map((section) => ({
    section: sectionLabels[section].split(' ')[0],
    current: report[section]?.score || 0,
    projected: report[section]?.projected_score || report[section]?.score || 0,
    fullMark: 100
  }));

  const barData = sections.filter(s => report[s]).map((section, index) => ({
    name: sectionLabels[section].split(' ')[0],
    current: report[section]?.score || 0,
    projected: report[section].projected_score,
    improvement: report[section].projected_score - report[section].score,
    grade: getGradeForView(section),
    color: sectionColors[index]
  }));

  const comparisonData = sections.map((section, index) => ({
    name: sectionLabels[section].split(' ')[0],
    before: report[section].score,
    after: report[section].projected_score,
    color: sectionColors[index]
  }));

  // Grade distribution for current view
  const getGradeDistribution = () => {
    return sections.reduce((acc, section) => {
      const grade = getGradeForView(section);
      const existing = acc.find(item => item.grade === grade);
      if (existing) existing.count++;
      else acc.push({ grade, count: 1, color: gradeColors[grade] });
      return acc;
    }, []);
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-3 rounded-lg shadow-lg border border-slate-200">
          <p className="font-semibold text-slate-900">{data.name}</p>
          <p className="text-sm text-slate-600">Current: <span className="font-bold">{data.current || data.before}%</span></p>
          {(data.projected || data.after) && (
            <p className="text-sm text-emerald-600">Projected: <span className="font-bold">{data.projected || data.after}%</span></p>
          )}
          {data.improvement && (
            <p className="text-sm text-blue-600">+{data.improvement} points</p>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      
      <main className="pt-20" ref={reportRef}>
        {/* Header */}
        <div className="bg-white border-b border-slate-200 sticky top-16 z-30 no-print">
          <div className="max-w-7xl mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Link to={isAdminSession() ? "/reports" : "/"}>
                  <Button variant="ghost" size="sm" className="text-slate-600" data-testid="back-btn">
                    <ArrowLeft className="h-4 w-4 mr-2" />Back
                  </Button>
                </Link>
                <Separator orientation="vertical" className="h-6" />
                <div>
                  <h1 className="text-xl font-semibold text-slate-900">{report.business_name}</h1>
                  <p className="text-sm text-slate-500">Generated {new Date(report.created_at).toLocaleDateString()}</p>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                {/* La Sales Report es de uso interno: el duenio del negocio que genero el
                    reporte no la ve. */}
                {isAdminSession() && (
                  <Link to={`/report/${reportId}/sales`}>
                    <Button variant="outline" size="sm" className="border-amber-300 text-amber-700 hover:bg-amber-50 hover:text-amber-700" data-testid="sales-report-btn">
                      <DollarSign className="h-4 w-4 mr-2" />Sales Report
                    </Button>
                  </Link>
                )}
                {isAdminSession() && report.business_id && (
                  <Link to={`/reviews/${report.business_id}`}>
                    <Button variant="outline" size="sm" data-testid="reviews-btn">
                      <MessageSquare className="h-4 w-4 mr-2" />Reviews
                    </Button>
                  </Link>
                )}
                <Button variant="outline" size="sm" onClick={handleShare}><Share2 className="h-4 w-4 mr-2" />Share</Button>
                <Button variant="outline" size="sm" onClick={handlePrint}><Printer className="h-4 w-4 mr-2" />Print</Button>
                <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={handlePrint}>
                  <Download className="h-4 w-4 mr-2" />Download PDF
                </Button>
              </div>
            </div>
          </div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="grid grid-cols-12 gap-8">
            {/* Side Navigation */}
            <aside className="col-span-3 no-print hidden lg:block">
              <div className="sticky-nav space-y-4">
                {/* Grade View Selector */}
                <Card className="border-slate-200" data-testid="grade-view-selector">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-slate-500 uppercase tracking-wider">
                      Grading View
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0 space-y-2">
                    <button
                      onClick={() => setGradeView("admin")}
                      className={`w-full text-left px-3 py-2.5 rounded-lg text-sm transition-all flex items-center gap-2 ${
                        gradeView === "admin" 
                          ? "bg-red-50 text-red-700 border border-red-200" 
                          : "text-slate-600 hover:bg-slate-50"
                      }`}
                      data-testid="view-admin"
                    >
                      <Lock className="h-4 w-4" />
                      <div>
                        <div className="font-medium">Grade A</div>
                        <div className="text-xs opacity-70">Client View (Strict)</div>
                      </div>
                    </button>
                    
                    <button
                      onClick={() => setGradeView("operations")}
                      className={`w-full text-left px-3 py-2.5 rounded-lg text-sm transition-all flex items-center gap-2 ${
                        gradeView === "operations" 
                          ? "bg-blue-50 text-blue-700 border border-blue-200" 
                          : "text-slate-600 hover:bg-slate-50"
                      }`}
                      data-testid="view-operations"
                    >
                      <Users className="h-4 w-4" />
                      <div>
                        <div className="font-medium">Grade O</div>
                        <div className="text-xs opacity-70">Internal Operations</div>
                      </div>
                    </button>
                    
                    <button
                      onClick={() => tieneProyeccion && setGradeView("comparison")}
                      disabled={!tieneProyeccion}
                      className={`w-full text-left px-3 py-2.5 rounded-lg text-sm transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                        gradeView === "comparison" 
                          ? "bg-emerald-50 text-emerald-700 border border-emerald-200" 
                          : "text-slate-600 hover:bg-slate-50"
                      }`}
                      data-testid="view-comparison"
                    >
                      <GitCompare className="h-4 w-4" />
                      <div>
                        <div className="font-medium">Grade C</div>
                        <div className="text-xs opacity-70">{tieneProyeccion ? "Before/After" : "Not available for this report"}</div>
                      </div>
                    </button>
                  </CardContent>
                </Card>

                {/* Section Navigation */}
                <Card className="border-slate-200">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium text-slate-500 uppercase tracking-wider">
                      Sections
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <nav className="space-y-1">
                      <button
                        onClick={() => scrollToSection("overview")}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                          activeSection === "overview" ? "bg-slate-100 text-slate-900" : "text-slate-600 hover:bg-slate-50"
                        }`}
                      >
                        Overview & Charts
                      </button>
                      
                      {sections.map((section, index) => {
                        const Icon = sectionIcons[section];
                        return (
                          <button
                            key={section}
                            onClick={() => scrollToSection(section)}
                            className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors flex items-center justify-between ${
                              activeSection === section ? "bg-slate-100 text-slate-900 font-medium" : "text-slate-600 hover:bg-slate-50"
                            }`}
                          >
                            <span className="flex items-center gap-2">
                              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: sectionColors[index] }}></span>
                              {sectionLabels[section]}
                            </span>
                            <GradeCard grade={getGradeForView(section)} size="xs" />
                          </button>
                        );
                      })}
                      
                      <button
                        onClick={() => scrollToSection("recommendations")}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                          activeSection === "recommendations" ? "bg-slate-100 text-slate-900" : "text-slate-600 hover:bg-slate-50"
                        }`}
                      >
                        <span className="flex items-center gap-2">
                          <Sparkles className="h-4 w-4" />
                          AI Recommendations
                        </span>
                      </button>
                      
                      <button
                        onClick={() => scrollToSection("budget")}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                          activeSection === "budget" ? "bg-emerald-100 text-emerald-900" : "text-slate-600 hover:bg-slate-50"
                        }`}
                      >
                        <span className="flex items-center gap-2">
                          <DollarSign className="h-4 w-4" />
                          Budget & Proposal
                        </span>
                      </button>
                    </nav>
                  </CardContent>
                </Card>
              </div>
            </aside>
            
            {/* Main Content */}
            <div className="col-span-12 lg:col-span-9 space-y-8">
              {/* Overview Section */}
              <section id="overview" className="report-section" data-testid="overview-section">
                {/* Grade View Indicator Banner */}
                <div className={`mb-6 p-4 rounded-xl border flex items-center justify-between ${
                  gradeView === "admin" ? "bg-red-50 border-red-200" :
                  gradeView === "operations" ? "bg-blue-50 border-blue-200" :
                  "bg-emerald-50 border-emerald-200"
                }`}>
                  <div className="flex items-center gap-3">
                    {gradeView === "admin" && <Lock className="h-5 w-5 text-red-600" />}
                    {gradeView === "operations" && <Users className="h-5 w-5 text-blue-600" />}
                    {gradeView === "comparison" && <GitCompare className="h-5 w-5 text-emerald-600" />}
                    <div>
                      <div className="font-semibold text-slate-900">
                        {gradeView === "admin" && "Grade A - Client View (Strict Standards)"}
                        {gradeView === "operations" && "Grade O - Internal Operations View"}
                        {gradeView === "comparison" && "Grade C - Comparison View (Before vs After)"}
                      </div>
                      <div className="text-sm text-slate-600">
                        {gradeView === "admin" && "Strict industry benchmarks shown to clients for transparency"}
                        {gradeView === "operations" && "Internal view for leadership planning and work prioritization"}
                        {gradeView === "comparison" && "Projected improvement after implementing recommendations"}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Hero Card */}
                <Card className="border-slate-200 overflow-hidden mb-6">
                  <div className={`p-8 text-white ${
                    gradeView === "admin" ? "bg-gradient-to-r from-red-600 via-rose-600 to-pink-600" :
                    gradeView === "operations" ? "bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-600" :
                    "bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600"
                  }`}>
                    <div className="flex items-start justify-between">
                      <div>
                        <h2 className="text-2xl font-bold mb-2">{report.business_name}</h2>
                        <p className="text-white/80 text-sm">
                          {gradeView === "comparison" ? "Before → After Analysis" : "Snapshot Report • Overall Performance"}
                        </p>
                      </div>
                      
                      {gradeView === "comparison" ? (
                        <div className="flex items-center gap-4">
                          <div className="text-center">
                            <div className="text-xs text-white/70 mb-1">Current</div>
                            <ScoreCircle score={report.overall_score} grade={report.overall_grade} size="md" />
                          </div>
                          <ArrowUpRight className="h-8 w-8 text-white/50" />
                          <div className="text-center">
                            <div className="text-xs text-white/70 mb-1">Projected</div>
                            <ScoreCircle score={report.projected_overall_score} grade={report.projected_overall_grade} size="md" />
                          </div>
                        </div>
                      ) : (
                        <ScoreCircle score={report.overall_score} grade={getOverallGrade()} size="lg" />
                      )}
                    </div>
                  </div>
                  
                  <CardContent className="p-6">
                    {/* Comparison View - Improvement Summary */}
                    {gradeView === "comparison" && (
                      <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-100 rounded-xl p-6 mb-6">
                        <div className="flex items-center gap-4 mb-4">
                          <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                            <TrendingUp className="h-6 w-6 text-emerald-600" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-slate-900">Projected Improvement</h3>
                            <p className="text-sm text-slate-600">After implementing our recommendations</p>
                          </div>
                        </div>
                        <div className="grid grid-cols-3 gap-4">
                          <div className="bg-white rounded-lg p-4 text-center">
                            <div className="text-3xl font-bold text-emerald-600">+{report.projected_overall_score - report.overall_score}</div>
                            <div className="text-sm text-slate-600">Points Gained</div>
                          </div>
                          <div className="bg-white rounded-lg p-4 text-center">
                            <div className="text-3xl font-bold text-emerald-600">
                              {report.overall_grade} → {report.projected_overall_grade}
                            </div>
                            <div className="text-sm text-slate-600">Grade Improvement</div>
                          </div>
                          <div className="bg-white rounded-lg p-4 text-center">
                            <div className="text-3xl font-bold text-emerald-600">
                              {Math.round(((report.projected_overall_score - report.overall_score) / report.overall_score) * 100)}%
                            </div>
                            <div className="text-sm text-slate-600">Performance Boost</div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* AI Summary */}
                    <div className={`rounded-xl p-6 mb-6 ${
                      gradeView === "admin" ? "bg-gradient-to-r from-red-50 to-rose-50 border border-red-100" :
                      gradeView === "operations" ? "bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100" :
                      "bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-100"
                    }`}>
                      <div className="flex items-start gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          gradeView === "admin" ? "bg-red-100" :
                          gradeView === "operations" ? "bg-blue-100" :
                          "bg-emerald-100"
                        }`}>
                          <Sparkles className={`h-5 w-5 ${
                            gradeView === "admin" ? "text-red-600" :
                            gradeView === "operations" ? "text-blue-600" :
                            "text-emerald-600"
                          }`} />
                        </div>
                        <div>
                          <h3 className="font-semibold text-slate-900 mb-2">AI Analysis Summary</h3>
                          <p className="text-slate-700 leading-relaxed">{report.ai_summary}</p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Grade Grid */}
                    <h3 className="font-semibold text-slate-900 mb-4">Section Grades</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {sections.map((section, index) => {
                        const Icon = sectionIcons[section];
                        const currentGrade = getGradeForView(section);
                        const projectedGrade = report[section].projected_grade;
                        
                        return (
                          <button
                            key={section}
                            onClick={() => scrollToSection(section)}
                            className="p-4 rounded-xl hover:shadow-md transition-all duration-300 text-left border-2"
                            style={{ 
                              backgroundColor: `${sectionColors[index]}10`,
                              borderColor: `${sectionColors[index]}30`
                            }}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <Icon className="h-5 w-5" style={{ color: sectionColors[index] }} />
                              {gradeView === "comparison" ? (
                                <div className="flex items-center gap-1">
                                  <GradeCard grade={report[section].grade} size="xs" />
                                  <ArrowUpRight className="h-3 w-3 text-emerald-500" />
                                  <GradeCard grade={projectedGrade} size="xs" />
                                </div>
                              ) : (
                                <GradeCard grade={currentGrade} size="sm" />
                              )}
                            </div>
                            <div className="text-sm font-medium text-slate-900">{sectionLabels[section]}</div>
                            {gradeView === "comparison" ? (
                              <div className="text-xs text-emerald-600">
                                {report[section].score}% → {report[section].projected_score}% (+{report[section].projected_score - report[section].score})
                              </div>
                            ) : (
                              <div className="text-xs text-slate-500">{report[section].score}%</div>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>

                {/* Charts */}
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  {/* Radar Chart */}
                  <Card className="border-slate-200">
                    <CardHeader className="pb-2">
                      <CardTitle className="flex items-center gap-2 text-lg">
                        <Target className="h-5 w-5 text-indigo-500" />
                        {gradeView === "comparison" ? "Before vs After" : "Performance Overview"}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={300}>
                        <RadarChart data={radarData}>
                          <PolarGrid stroke="#E2E8F0" />
                          <PolarAngleAxis dataKey="section" tick={{ fill: '#64748B', fontSize: 11 }} />
                          <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fill: '#94A3B8', fontSize: 10 }} />
                          <Radar name="Current" dataKey="current" stroke="#6366F1" fill="#6366F1" fillOpacity={0.4} strokeWidth={2} />
                          {gradeView === "comparison" && (
                            <Radar name="Projected" dataKey="projected" stroke="#10B981" fill="#10B981" fillOpacity={0.3} strokeWidth={2} />
                          )}
                        </RadarChart>
                      </ResponsiveContainer>
                      {gradeView === "comparison" && (
                        <div className="flex justify-center gap-6 mt-2">
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full bg-indigo-500"></div>
                            <span className="text-sm text-slate-600">Current</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
                            <span className="text-sm text-slate-600">Projected</span>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Bar Chart - Comparison or Score */}
                  <Card className="border-slate-200">
                    <CardHeader className="pb-2">
                      <CardTitle className="flex items-center gap-2 text-lg">
                        <BarChart3 className="h-5 w-5 text-violet-500" />
                        {gradeView === "comparison" ? "Improvement by Section" : "Score Comparison"}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={300}>
                        {gradeView === "comparison" ? (
                          <ComposedChart data={comparisonData} layout="vertical">
                            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                            <XAxis type="number" domain={[0, 100]} tick={{ fill: '#64748B', fontSize: 11 }} />
                            <YAxis type="category" dataKey="name" width={80} tick={{ fill: '#64748B', fontSize: 11 }} />
                            <Tooltip content={<CustomTooltip />} />
                            <Bar dataKey="before" fill="#94A3B8" radius={[0, 4, 4, 0]} name="Before" />
                            <Bar dataKey="after" fill="#10B981" radius={[0, 4, 4, 0]} name="After" />
                          </ComposedChart>
                        ) : (
                          <BarChart data={barData} layout="vertical">
                            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                            <XAxis type="number" domain={[0, 100]} tick={{ fill: '#64748B', fontSize: 11 }} />
                            <YAxis type="category" dataKey="name" width={80} tick={{ fill: '#64748B', fontSize: 11 }} />
                            <Tooltip content={<CustomTooltip />} />
                            <Bar dataKey="current" radius={[0, 4, 4, 0]}>
                              {barData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.color} />
                              ))}
                            </Bar>
                          </BarChart>
                        )}
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </div>

                {/* Grade Distribution */}
                <Card className="border-slate-200">
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Star className="h-5 w-5 text-yellow-500" />
                      Grade Distribution ({gradeView === "admin" ? "Strict" : gradeView === "operations" ? "Client-Ready" : "Comparison"})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-8">
                      <div className="w-48 h-48">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={getGradeDistribution()}
                              cx="50%"
                              cy="50%"
                              innerRadius={40}
                              outerRadius={70}
                              dataKey="count"
                              nameKey="grade"
                              label={({ grade }) => grade}
                              labelLine={false}
                            >
                              {getGradeDistribution().map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.color} />
                              ))}
                            </Pie>
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                      <div className="flex-1">
                        <div className="grid grid-cols-5 gap-3">
                          {['A', 'B', 'C', 'D', 'F'].map((grade) => {
                            const count = sections.filter(s => getGradeForView(s) === grade).length;
                            return (
                              <div 
                                key={grade}
                                className="text-center p-3 rounded-xl"
                                style={{ backgroundColor: `${gradeColors[grade]}15` }}
                              >
                                <div className="text-2xl font-bold" style={{ color: gradeColors[grade] }}>{count}</div>
                                <div className="text-xs text-slate-600">Grade {grade}</div>
                              </div>
                            );
                          })}
                        </div>
                        <p className="text-sm text-slate-500 mt-4">
                          {gradeView === "admin" && "Strict industry-standard assessment. Use for internal planning."}
                          {gradeView === "operations" && "Achievable benchmarks showing realistic growth potential."}
                          {gradeView === "comparison" && `Projected to move from ${report.overall_grade} to ${report.projected_overall_grade} with our services.`}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </section>
              
              {/* Section Cards */}
              {sections.map((section, index) => (
                <SectionCard
                  key={section}
                  id={section}
                  title={sectionLabels[section]}
                  icon={sectionIcons[section]}
                  data={report[section]}
                  color={sectionColors[index]}
                  gradeView={gradeView}
                />
              ))}
              
              {/* AI Recommendations */}
              <section id="recommendations" className="report-section">
                <Card className="border-slate-200 overflow-hidden">
                  <div className="h-2 bg-gradient-to-r from-blue-500 via-indigo-500 to-violet-500"></div>
                  <CardHeader className="border-b border-slate-100">
                    <CardTitle className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center">
                        <Sparkles className="h-5 w-5 text-white" />
                      </div>
                      AI-Powered Recommendations
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-6">
                    <p className="text-slate-600 mb-6">
                      Implementing these recommendations can improve your score from{" "}
                      <span className="font-bold text-slate-900">{report.overall_score}%</span> to{" "}
                      <span className="font-bold text-emerald-600">{report.projected_overall_score}%</span>
                    </p>
                    
                    <div className="space-y-4">
                      {report.ai_recommendations.map((rec, index) => {
                        const colors = ['from-blue-500 to-indigo-500', 'from-violet-500 to-purple-500', 'from-pink-500 to-rose-500', 'from-orange-500 to-amber-500', 'from-emerald-500 to-teal-500'];
                        return (
                          <div key={index} className="flex items-start gap-4 p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors">
                            <div className={`w-8 h-8 bg-gradient-to-br ${colors[index % colors.length]} rounded-lg flex items-center justify-center flex-shrink-0`}>
                              <span className="text-sm font-bold text-white">{index + 1}</span>
                            </div>
                            <p className="text-slate-800 font-medium">{rec}</p>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              </section>
              
              {/* Budget & Proposal Section */}
              <section id="budget" className="report-section" data-testid="budget-section">
                <BudgetMaker budget={report.proposed_budget} businessName={report.business_name} />
              </section>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
